import {
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Grid2X2,
  Images,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  categories,
  heroProductId,
  products,
  type CategoryId,
  type Product,
} from "./data/catalog";

const ALL_CATEGORIES = "all" as const;

function productFromHash(): Product | null {
  const id = decodeURIComponent(window.location.hash.slice(1));
  return products.find((product) => product.id === id) ?? null;
}

function App() {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<
    CategoryId | typeof ALL_CATEGORIES
  >(ALL_CATEGORIES);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(() =>
    productFromHash(),
  );
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const lastTriggerRef = useRef<HTMLElement | null>(null);
  const modalHistoryEntryRef = useRef(false);
  const touchStartXRef = useRef<number | null>(null);

  const heroProduct =
    products.find((product) => product.id === heroProductId) ?? products[0];

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("zh-CN");

    return products.filter((product) => {
      const matchesCategory =
        activeCategory === ALL_CATEGORIES ||
        product.categoryId === activeCategory;
      const matchesQuery =
        normalizedQuery.length === 0 ||
        product.name.toLocaleLowerCase("zh-CN").includes(normalizedQuery);

      return matchesCategory && matchesQuery;
    });
  }, [activeCategory, query]);

  useEffect(() => {
    const handleLocationChange = () => {
      const product = productFromHash();
      setSelectedProduct(product);
      setSelectedImageIndex(0);
      if (!product) {
        modalHistoryEntryRef.current = false;
        window.requestAnimationFrame(() => lastTriggerRef.current?.focus());
      }
    };

    window.addEventListener("hashchange", handleLocationChange);
    window.addEventListener("popstate", handleLocationChange);
    return () => {
      window.removeEventListener("hashchange", handleLocationChange);
      window.removeEventListener("popstate", handleLocationChange);
    };
  }, []);

  const closeProduct = useCallback(() => {
    if (!selectedProduct) return;

    if (modalHistoryEntryRef.current) {
      modalHistoryEntryRef.current = false;
      window.history.back();
      setSelectedProduct(null);
      setSelectedImageIndex(0);
      window.requestAnimationFrame(() => lastTriggerRef.current?.focus());
      return;
    }

    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}`,
    );
    setSelectedProduct(null);
    setSelectedImageIndex(0);
    window.requestAnimationFrame(() => lastTriggerRef.current?.focus());
  }, [selectedProduct]);

  const showPreviousImage = useCallback(() => {
    if (!selectedProduct || selectedProduct.images.length < 2) return;
    setSelectedImageIndex((current) =>
      current === 0 ? selectedProduct.images.length - 1 : current - 1,
    );
  }, [selectedProduct]);

  const showNextImage = useCallback(() => {
    if (!selectedProduct || selectedProduct.images.length < 2) return;
    setSelectedImageIndex(
      (current) => (current + 1) % selectedProduct.images.length,
    );
  }, [selectedProduct]);

  useEffect(() => {
    if (!selectedProduct) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => dialogRef.current?.focus());

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeProduct();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        showPreviousImage();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        showNextImage();
      } else if (event.key === "Tab" && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
          ),
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeProduct, selectedProduct, showNextImage, showPreviousImage]);

  const openProduct = (product: Product, trigger: HTMLElement) => {
    lastTriggerRef.current = trigger;
    modalHistoryEntryRef.current = true;
    window.location.hash = product.id;
    setSelectedProduct(product);
    setSelectedImageIndex(0);
  };

  const clearFilters = () => {
    setQuery("");
    setActiveCategory(ALL_CATEGORIES);
  };

  const selectedImage = selectedProduct?.images[selectedImageIndex];

  return (
    <>
      <a className="skip-link" href="#catalog">
        跳到全部款式
      </a>

      <header className="site-header">
        <a className="wordmark" href="#top" aria-label="平价手工贡品首页">
          <span className="wordmark-seal" aria-hidden="true">
            平
          </span>
          <span>
            <strong>平价手工贡品</strong>
            <small>民俗手作 · 款式陈列</small>
          </span>
        </a>
        <a className="header-link" href="#catalog">
          浏览全部款式
          <ArrowDown size={17} strokeWidth={1.8} aria-hidden="true" />
        </a>
      </header>

      <main id="top">
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-copy">
            <p className="eyebrow">
              <Sparkles size={16} strokeWidth={1.7} aria-hidden="true" />
              一份心意 · 一份讲究
            </p>
            <h1 id="hero-title">
              平价不减心意，
              <span>手作自有温度</span>
            </h1>
            <p className="hero-lead">
              汇集楼房宅院、摇钱树、聚宝盆、金山钱粮等多种手工作品，按类别轻松浏览每一种样式。
            </p>
            <a className="primary-action" href="#catalog">
              查看全部作品
              <ArrowDown size={18} aria-hidden="true" />
            </a>
            <dl className="hero-stats" aria-label="作品数量">
              <div>
                <dt>57</dt>
                <dd>款作品</dd>
              </div>
              <div>
                <dt>7</dt>
                <dd>大分类</dd>
              </div>
              <div>
                <dt>109</dt>
                <dd>张实拍</dd>
              </div>
            </dl>
          </div>

          {heroProduct && (
            <div className="hero-showcase" aria-label={`精选作品：${heroProduct.name}`}>
              <div className="hero-frame">
                <span className="frame-corner frame-corner-top" />
                <img
                  src={heroProduct.images[0].src}
                  srcSet={heroProduct.images[0].srcSet}
                  sizes="(max-width: 760px) 88vw, 46vw"
                  width={heroProduct.images[0].width}
                  height={heroProduct.images[0].height}
                  alt={heroProduct.images[0].alt}
                  loading="eager"
                  fetchPriority="high"
                />
                <span className="frame-corner frame-corner-bottom" />
              </div>
              <div className="hero-caption">
                <span>精选款式</span>
                <strong>{heroProduct.name}</strong>
              </div>
            </div>
          )}
        </section>

        <section className="catalog-section" id="catalog" aria-labelledby="catalog-title">
          <div className="section-heading">
            <div>
              <p className="eyebrow">作品陈列</p>
              <h2 id="catalog-title">按心意，选款式</h2>
            </div>
            <p>所有图片均为实物样式展示，点击任一作品可查看完整大图。</p>
          </div>

          <div className="catalog-toolbar">
            <div className="search-field">
              <Search size={19} strokeWidth={1.8} aria-hidden="true" />
              <label className="sr-only" htmlFor="product-search">
                搜索作品名称
              </label>
              <input
                id="product-search"
                type="search"
                aria-label="搜索作品名称"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="搜索作品名称"
                autoComplete="off"
              />
              {query && (
                <button
                  className="clear-search"
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="清除搜索"
                >
                  <X size={17} aria-hidden="true" />
                </button>
              )}
            </div>

            <div className="category-scroller" aria-label="作品分类">
              <button
                type="button"
                className={activeCategory === ALL_CATEGORIES ? "active" : ""}
                aria-pressed={activeCategory === ALL_CATEGORIES}
                onClick={() => setActiveCategory(ALL_CATEGORIES)}
              >
                全部 <span>{products.length}</span>
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  className={activeCategory === category.id ? "active" : ""}
                  aria-pressed={activeCategory === category.id}
                  onClick={() => setActiveCategory(category.id)}
                >
                  {category.name} <span>{category.count}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="results-line" role="status" aria-live="polite">
            <span>
              <Grid2X2 size={17} strokeWidth={1.8} aria-hidden="true" />
              当前显示 <strong>{filteredProducts.length}</strong> 款
            </span>
            {(query || activeCategory !== ALL_CATEGORIES) && (
              <button type="button" onClick={clearFilters}>
                清除筛选
              </button>
            )}
          </div>

          {filteredProducts.length > 0 ? (
            <div className="product-grid">
              {filteredProducts.map((product) => {
                const category = categories.find(
                  (item) => item.id === product.categoryId,
                );
                const image = product.images[0];

                return (
                  <article className="product-card" key={product.id}>
                    <button
                      type="button"
                      className="product-open"
                      onClick={(event) => openProduct(product, event.currentTarget)}
                      aria-label={`查看${product.name}大图，共${product.images.length}张`}
                    >
                      <span className="product-image-stage">
                        <img
                          src={image.src}
                          srcSet={image.srcSet}
                          sizes="(max-width: 540px) 46vw, (max-width: 900px) 31vw, 24vw"
                          width={image.width}
                          height={image.height}
                          alt={image.alt}
                          loading="lazy"
                          decoding="async"
                        />
                        <span className="image-count">
                          <Images size={14} aria-hidden="true" />
                          {product.images.length}
                        </span>
                      </span>
                      <span className="product-card-copy">
                        <small>{category?.name}</small>
                        <strong>{product.name}</strong>
                        <span className="view-detail">
                          查看大图 <ChevronRight size={15} aria-hidden="true" />
                        </span>
                      </span>
                    </button>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <Search size={28} strokeWidth={1.4} aria-hidden="true" />
              <h3>暂未找到相关款式</h3>
              <p>换一个名称，或者清除筛选后再看看。</p>
              <button type="button" onClick={clearFilters}>
                查看全部作品
              </button>
            </div>
          )}
        </section>
      </main>

      <footer className="site-footer">
        <div>
          <span className="wordmark-seal" aria-hidden="true">
            平
          </span>
          <div>
            <strong>平价手工贡品</strong>
            <p>图片仅供样式展示，作品以实物为准。</p>
          </div>
        </div>
        <a href="#top">回到顶部</a>
      </footer>

      {selectedProduct && selectedImage && (
        <div
          className="modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeProduct();
          }}
        >
          <div
            className="product-dialog"
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="dialog-title"
            tabIndex={-1}
          >
            <button
              className="dialog-close"
              type="button"
              onClick={closeProduct}
              aria-label="关闭大图"
            >
              <X size={23} aria-hidden="true" />
            </button>

            <div
              className="dialog-stage"
              onTouchStart={(event) => {
                touchStartXRef.current = event.changedTouches[0]?.clientX ?? null;
              }}
              onTouchEnd={(event) => {
                const startX = touchStartXRef.current;
                const endX = event.changedTouches[0]?.clientX;
                touchStartXRef.current = null;
                if (startX == null || endX == null) return;
                const delta = endX - startX;
                if (Math.abs(delta) < 48) return;
                if (delta > 0) showPreviousImage();
                else showNextImage();
              }}
            >
              <img
                key={selectedImage.src}
                src={selectedImage.src}
                srcSet={selectedImage.srcSet}
                sizes="(max-width: 760px) 100vw, 72vw"
                width={selectedImage.width}
                height={selectedImage.height}
                alt={selectedImage.alt}
                decoding="async"
              />

              {selectedProduct.images.length > 1 && (
                <>
                  <button
                    className="dialog-arrow dialog-arrow-left"
                    type="button"
                    onClick={showPreviousImage}
                    aria-label="查看上一张图片"
                  >
                    <ChevronLeft size={25} aria-hidden="true" />
                  </button>
                  <button
                    className="dialog-arrow dialog-arrow-right"
                    type="button"
                    onClick={showNextImage}
                    aria-label="查看下一张图片"
                  >
                    <ChevronRight size={25} aria-hidden="true" />
                  </button>
                </>
              )}
            </div>

            <div className="dialog-copy">
              <div>
                <span>
                  {
                    categories.find(
                      (category) => category.id === selectedProduct.categoryId,
                    )?.name
                  }
                </span>
                <h2 id="dialog-title">{selectedProduct.name}</h2>
              </div>
              <div className="image-selector" aria-label="选择图片">
                {selectedProduct.images.map((image, index) => (
                  <button
                    key={image.src}
                    type="button"
                    className={selectedImageIndex === index ? "active" : ""}
                    aria-pressed={selectedImageIndex === index}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    {image.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default App;
